import Phaser from 'phaser';
import { Preferences } from '../WebPreferences';
import { musicManager } from '../audio/MusicManager';
import { showRewarded, showInterstitial, showBanner } from '../AdManager';
import { submitScore } from '../LeaderboardManager';
import { getAdsRemoved } from '../IAPManager';

export default class UIScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UIScene' });
    this.highFoods         = 0;
    this.highHeight        = 0;
    this.gameOverContainer = null;
    this.rotateBtn         = null;
    this.rotateBtnText     = null;
    this.rotateBtnHit      = null;
    this.foodText          = null;
    this.heightText        = null;
    this.bestFoodsText     = null;
    this.bestHeightText    = null;
    this.pauseContainer    = null;
    this.gameMode          = 'solo';
    this.turnIndicator     = null;
    this.botScoreText      = null;
    this.rescueContainer   = null;
    this.rotateAdContainer = null;
    this.previewFoodContainer    = null;
    this.nextFoodRevealContainer = null;
    this.previewNextBtn    = null;
    this.qualifyingLosses  = 0;
  }

  create(data) {
    showBanner();

    this.gameMode = (data && data.mode === 'vs_bot') ? 'vs_bot' : 'solo';
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    // ── HUD layout constants ──────────────────────────────────────
    const cardY   = 52 * sd;
    const LEFT_X  = Math.min(140 * sd, W * 0.32) / 2 + 10 * sd;
    const RIGHT_X = W - Math.min(140 * sd, W * 0.32) / 2 - 10 * sd;

    const lbl = (x, y, txt, color) => this.add.text(x, y, txt, {
      fontSize:        `${13 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'italic',
      color,
      stroke:          '#000000',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5, 0).setDepth(10);

    const val = (x, y, txt, color) => this.add.text(x, y, txt, {
      fontSize:        `${28 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold',
      color,
      stroke:          '#7711aa',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5, 0).setDepth(10);

    if (this.gameMode === 'vs_bot') {
      // ── VS BOT HUD: You score (left) + Bot score (right) ─────────
      lbl(LEFT_X,  cardY + 8 * sd,  'You 🍕',  '#ddbbff');
      this.foodText = val(LEFT_X, cardY + 30 * sd, '0', '#ddbbff');

      lbl(RIGHT_X, cardY + 8 * sd,  'Bot 🤖',  '#ffbb88');
      this.botScoreText = val(RIGHT_X, cardY + 30 * sd, '0', '#ffbb88');

      this.heightText      = null;
      this.bestFoodsText   = null;
      this.bestHeightText  = null;
    } else {
      // ── Solo HUD ──────────────────────────────────────────────────
      lbl(LEFT_X, cardY + 8 * sd,  'Foods 🍕',      '#ddbbff');
      this.foodText = val(LEFT_X, cardY + 30 * sd, '0', '#ffffff');

      lbl(LEFT_X, cardY + 68 * sd, 'Height 📏',     '#cce4ff');
      this.heightText = this.add.text(LEFT_X, cardY + 90 * sd, '0', {
        fontSize: `${28 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'bold', color: '#ffffff', stroke: '#7733bb', strokeThickness: 4 * dpr,
      }).setOrigin(0.5, 0).setDepth(10);

      lbl(RIGHT_X, cardY + 8 * sd,  '🏆 Best Foods',  '#ddbbff');
      this.bestFoodsText = val(RIGHT_X, cardY + 30 * sd, `${this.highFoods}`, '#ffffff');

      lbl(RIGHT_X, cardY + 68 * sd, '🏆 Best Height', '#cce4ff');
      this.bestHeightText = this.add.text(RIGHT_X, cardY + 90 * sd, `${this.highHeight}`, {
        fontSize: `${28 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'bold', color: '#ffffff', stroke: '#7733bb', strokeThickness: 4 * dpr,
      }).setOrigin(0.5, 0).setDepth(10);

      // Update best text once Preferences load
      Preferences.get({ key: 'highFoods'  }).then(r => {
        this.highFoods = parseInt(r.value) || 0;
        if (this.bestFoodsText) this.bestFoodsText.setText(`${this.highFoods}`);
      });
      Preferences.get({ key: 'highHeight' }).then(r => {
        this.highHeight = parseInt(r.value) || 0;
        if (this.bestHeightText) this.bestHeightText.setText(`${this.highHeight}`);
      });
    }

    // ── Hint text ─────────────────────────────────────────────────
    this.add.text(W / 2, cardY + 15 * sd, 'Tap to drop  •  Drag to aim', {
      fontSize:        `${12 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'italic',
      color:           '#ffffff',
      stroke:          '#000000',
      strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(10);

    // ── Turn indicator (VS BOT mode only) ─────────────────────────
    this.turnIndicator = this.add.text(W / 2, cardY + 32 * sd, 'YOUR TURN', {
      fontSize:        `${14 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#aaffcc',
      stroke:          '#000000',
      strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(10).setVisible(this.gameMode === 'vs_bot');

    // ── Rotate button — centred at bottom (raised for banner ad) ──
    const btnW = Math.min(160 * sd, W * 0.4);
    const btnH = 65 * sd;
    const btnY = H - btnH / 2 - 20 * sd;
    const btnRadius = 20 * sd;

    const drawRotateBtn = (color) => {
      this.rotateBtn.clear();
      this.rotateBtn.fillStyle(color, 1);
      this.rotateBtn.fillRoundedRect(W / 2 - btnW / 2, btnY - btnH / 2, btnW, btnH, btnRadius);
      this.rotateBtn.fillStyle(0xffffff, 0.20);
      this.rotateBtn.fillRoundedRect(W / 2 - btnW / 2 + 5 * sd, btnY - btnH / 2 + 4 * sd, btnW - 10 * sd, btnH * 0.38, btnRadius * 0.8);
    };

    this.rotateBtn = this.add.graphics().setDepth(10);
    drawRotateBtn(0x9944cc);

    this.rotateBtnHit = this.add.rectangle(W / 2, btnY, btnW, btnH)
      .setInteractive({ useHandCursor: true })
      .setDepth(10);
    const rotateBtnHit = this.rotateBtnHit;

    this.rotateBtnText = this.add.text(W / 2, btnY, '🔄  Rotate (12)', {
      fontSize: `${22 * sd}px`,
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color: '#ffffff', stroke: '#7711aa', strokeThickness: 2 * dpr,
    }).setOrigin(0.5).setDepth(11);

    // Update button text if ads already removed
    getAdsRemoved().then(adsRemoved => {
      if (adsRemoved && this.rotateBtnText) {
        this.rotateBtnText.setText('🔄  Rotate (∞)');
      }
    });

    rotateBtnHit.on('pointerover',  () => drawRotateBtn(0xbb77ff));
    rotateBtnHit.on('pointerout',   () => drawRotateBtn(0x9944cc));
    rotateBtnHit.on('pointerdown',  () => {
      this.tweens.add({
        targets: [this.rotateBtnText],
        scaleX: 0.93, scaleY: 0.93, duration: 70, yoyo: true,
      });
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.rotatePreview();
    });
    // ── Pause button — bottom left (circle pill) ──────────────────
    const pr   = 28 * sd;          // radius
    const pbX  = pr + 16 * sd;
    const pbY  = btnY;

    const pauseGfx = this.add.graphics().setDepth(10);
    const drawPauseBtn = (col) => {
      pauseGfx.clear();
      // outer soft glow ring
      pauseGfx.fillStyle(0xcc99ff, 0.22);
      pauseGfx.fillCircle(pbX, pbY, pr * 1.38);
      // main circle
      pauseGfx.fillStyle(col, 1);
      pauseGfx.fillCircle(pbX, pbY, pr);
      // gloss highlight
      pauseGfx.fillStyle(0xffffff, 0.28);
      pauseGfx.fillEllipse(pbX - pr * 0.12, pbY - pr * 0.30, pr * 1.0, pr * 0.50);
      // thin white rim
      pauseGfx.lineStyle(1.5 * dpr, 0xffffff, 0.55);
      pauseGfx.strokeCircle(pbX, pbY, pr);
    };
    drawPauseBtn(0xbb88ee);

    const pauseHit = this.add.circle(pbX, pbY, pr * 1.4)
      .setInteractive({ useHandCursor: true })
      .setDepth(10)
      .setAlpha(0.001);

    this.add.text(pbX, pbY, '⏸', {
      fontSize:   `${22 * sd}px`,
      fontFamily: 'Arial, sans-serif',
      color:      '#ffffff',
    }).setOrigin(0.5).setDepth(11);

    pauseHit.on('pointerover',  () => drawPauseBtn(0xcc99ff));
    pauseHit.on('pointerout',   () => drawPauseBtn(0xbb88ee));
    pauseHit.on('pointerdown',  () => {
      drawPauseBtn(0x9966cc);
      this.tweens.add({ targets: pauseGfx, scaleX: 0.90, scaleY: 0.90, duration: 70, yoyo: true });
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.pauseGame();
    });

    // ── Preview-next button — bottom right (circle pill) ──────────
    const prv  = 28 * sd;          // radius
    const prvX = W - prv - 16 * sd;
    const prvY = btnY;

    const previewGfx = this.add.graphics().setDepth(10);
    let previewBtnColor = 0xbb88ee;
    const drawPreviewBtn = (col) => {
      previewGfx.clear();
      // outer soft glow ring
      previewGfx.fillStyle(0xcc99ff, 0.22);
      previewGfx.fillCircle(prvX, prvY, prv * 1.38);
      // main circle
      previewGfx.fillStyle(col, 1);
      previewGfx.fillCircle(prvX, prvY, prv);
      // gloss highlight
      previewGfx.fillStyle(0xffffff, 0.28);
      previewGfx.fillEllipse(prvX - prv * 0.12, prvY - prv * 0.30, prv * 1.0, prv * 0.50);
      // thin white rim
      previewGfx.lineStyle(1.5 * dpr, 0xffffff, 0.55);
      previewGfx.strokeCircle(prvX, prvY, prv);
    };
    drawPreviewBtn(previewBtnColor);

    const previewHit = this.add.circle(prvX, prvY, prv * 1.4)
      .setInteractive({ useHandCursor: true })
      .setDepth(10)
      .setAlpha(0.001);

    this.add.text(prvX, prvY, '🔮', {
      fontSize:   `${22 * sd}px`,
      fontFamily: 'Arial, sans-serif',
      color:      '#ffffff',
    }).setOrigin(0.5).setDepth(11);

    this.previewNextBtn = previewGfx;

    // Gold tint if ads removed (premium = instant reveal)
    getAdsRemoved().then(adsRemoved => {
      if (adsRemoved) {
        previewBtnColor = 0xddaa00;
        drawPreviewBtn(previewBtnColor);
      }
    });

    previewHit.on('pointerover',  () => drawPreviewBtn(0xcc99ff));
    previewHit.on('pointerout',   () => drawPreviewBtn(previewBtnColor));
    previewHit.on('pointerdown',  async () => {
      drawPreviewBtn(0x9966cc);
      this.tweens.add({ targets: previewGfx, scaleX: 0.90, scaleY: 0.90, duration: 70, yoyo: true });

      const adsRemoved = await getAdsRemoved();
      const gameScene  = this.scene.get('GameScene');

      if (adsRemoved) {
        // Instant reveal without ad
        if (gameScene) {
          const nextFood = gameScene.getNextFoodName();
          this.showNextFoodReveal(nextFood, sd, dpr);
          gameScene.nextFoodRevealed = true;
          gameScene.events.emit('nextFoodStatusUpdate', true);
        }
      } else {
        // Show ad offer
        this.showPreviewNextFoodOffer();
      }
    });

    // ── GameScene event listeners ─────────────────────────────────
    const game = this.scene.get('GameScene');

    if (game) {
      game.events.off('scoreUpdate').on('scoreUpdate', score => {
        if (this.foodText) this.foodText.setText(`${score}`);
      });

      game.events.off('heightUpdate').on('heightUpdate', height => {
        if (this.heightText) this.heightText.setText(`${height}`);
      });

      game.events.off('playerScoreUpdate').on('playerScoreUpdate', score => {
        if (this.foodText) this.foodText.setText(`${score}`);
      });

      game.events.off('botScoreUpdate').on('botScoreUpdate', score => {
        if (this.botScoreText) this.botScoreText.setText(`${score}`);
      });

      game.events.off('gameOver').on('gameOver', ({ foods, towerHeight, loser, playerScore, botScore }) => {
        if (this.gameMode === 'solo') {
          if (foods > this.highFoods) {
            this.highFoods = foods;
            Preferences.set({ key: 'highFoods', value: String(foods) });
            if (this.bestFoodsText) this.bestFoodsText.setText(`${this.highFoods}`);
          }
          if (towerHeight > this.highHeight) {
            this.highHeight = towerHeight;
            Preferences.set({ key: 'highHeight', value: String(towerHeight) });
            if (this.bestHeightText) this.bestHeightText.setText(`${this.highHeight}`);
          }
          submitScore(foods, towerHeight);
        }
        this.showGameOver(foods, towerHeight, loser, playerScore, botScore);
      });

      game.events.off('turnChanged').on('turnChanged', (turn) => {
        if (!this.turnIndicator) return;
        if (turn === 'player') {
          this.turnIndicator.setText('YOUR TURN').setColor('#aaffcc');
        } else {
          this.turnIndicator.setText("BOT'S TURN").setColor('#ffbb88');
        }
        if (this.gameMode === 'vs_bot') {
          const isPlayer = turn === 'player';
          if (this.rotateBtn)     this.rotateBtn.setVisible(isPlayer);
          if (this.rotateBtnText) this.rotateBtnText.setVisible(isPlayer);
          if (this.rotateBtnHit)  this.rotateBtnHit.setVisible(isPlayer);
          if (this.previewNextBtn) this.previewNextBtn.setVisible(isPlayer);
        }
      });

      game.events.off('gameRestarted').on('gameRestarted', () => {
        if (this.foodText)    this.foodText.setText('0');
        if (this.heightText)  this.heightText.setText('0');
        if (this.botScoreText) this.botScoreText.setText('0');
        if (this.gameOverContainer) {
          this.gameOverContainer.destroy(true);
          this.gameOverContainer = null;
        }
        if (this.rotateAdContainer) {
          this.rotateAdContainer.destroy(true);
          this.rotateAdContainer = null;
        }
        if (this.previewFoodContainer) {
          this.previewFoodContainer.destroy(true);
          this.previewFoodContainer = null;
        }
        if (this.nextFoodRevealContainer) {
          this.nextFoodRevealContainer.destroy(true);
          this.nextFoodRevealContainer = null;
        }
        if (this.previewNextBtn) this.previewNextBtn.setVisible(true);
        if (this.rotateBtn)     this.rotateBtn.setVisible(true);
        if (this.rotateBtnText) this.rotateBtnText.setVisible(true);
        if (this.rotateBtnHit)  this.rotateBtnHit.setVisible(true);
        if (this.turnIndicator && this.gameMode === 'vs_bot') {
          this.turnIndicator.setText('YOUR TURN').setColor('#aaffcc').setVisible(true);
        }
      });

      game.events.off('rotationCountUpdate').on('rotationCountUpdate', async (count) => {
        if (this.rotateBtnText) {
          const adsRemoved = await getAdsRemoved();
          if (adsRemoved) {
            this.rotateBtnText.setText('🔄  Rotate (∞)');
          } else {
            this.rotateBtnText.setText(`🔄  Rotate (${count})`);
          }
        }
      });

      game.events.off('rotationsExhausted').on('rotationsExhausted', () => {
        this.showRotateAdOffer();
      });

      game.events.off('nextFoodStatusUpdate').on('nextFoodStatusUpdate', (revealed) => {
        if (this.previewNextBtn) {
          this.previewNextBtn.setVisible(!revealed);
        }
      });

      game.events.off('gamePaused').on('gamePaused', () => {
        this.showPaused();
      });

      game.events.off('gameResumed').on('gameResumed', () => {
        if (this.pauseContainer) {
          this.pauseContainer.destroy(true);
          this.pauseContainer = null;
        }
      });

      game.events.off('offerRescue').on('offerRescue', () => {
        this.showRescueOffer();
      });

      game.events.off('rescueComplete').on('rescueComplete', ({ score }) => {
        if (this.foodText)  this.foodText.setText(`${score}`);
        if (this.rotateBtn)     this.rotateBtn.setVisible(true);
        if (this.rotateBtnText) this.rotateBtnText.setVisible(true);
        if (this.rotateBtnHit)  this.rotateBtnHit.setVisible(true);
        if (this.previewNextBtn) this.previewNextBtn.setVisible(true);
      });

    }
  }

  /* ─────────────────────── GAME OVER SCREEN ────────────────────── */
  showGameOver(foods, towerHeight, loser, playerScore, botScore) {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.rotateBtn)     this.rotateBtn.setVisible(false);
    if (this.rotateBtnText) this.rotateBtnText.setVisible(false);
    if (this.previewNextBtn) this.previewNextBtn.setVisible(false);

    this.gameOverContainer = this.add.container(0, 0).setDepth(20);

    const isBot   = this.gameMode === 'vs_bot';
    const cardW   = Math.min(320 * sd, W * 0.85);
    const gap     = 52 * sd;
    const titleY  = H / 2 - gap * (isBot ? 2.0 : 2.5);
    const lbl1Y   = titleY   + gap;
    const val1Y   = lbl1Y    + gap;
    const lbl2Y   = val1Y    + gap;
    const val2Y   = lbl2Y    + gap;
    const playBtnY = isBot ? val1Y + gap : val2Y + gap;
    const homeBtnY = playBtnY + gap * 1.5;

    const cardH       = homeBtnY - titleY + gap * 2.0;
    const cardCentreY = (titleY + homeBtnY) / 2 + gap * 0.2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2;
    const cr          = 22 * sd;

    const overlay = this.add.graphics();
    overlay.fillStyle(0xf8f0ff, 0.82);
    overlay.fillRect(0, 0, W, H);

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * dpr, 0xbb88ee, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0xaa66dd, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    // ── Title ──────────────────────────────────────────────────────
    let titleStr, titleColor;
    if (loser === 'player') {
      titleStr  = '😢 You Lost!';
      titleColor = '#cc4444';
    } else if (loser === 'bot') {
      titleStr  = '🎉 You Win!';
      titleColor = '#44aa66';
    } else {
      titleStr  = '🍭 Tower Fell! 🍬';
      titleColor = '#7722aa';
    }

    const title = this.add.text(W / 2, titleY, titleStr, {
      fontSize:        `${25 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           titleColor,
      stroke:          '#eeddff',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    const allItems = [overlay, shadow, card, strip, title];

    if (isBot) {
      // ── VS BOT card: You vs Bot scores ───────────────────────────
      const lbl1 = this.add.text(W / 2, lbl1Y, 'YOU  vs  BOT', {
        fontSize: `${13 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', color: '#9966cc',
      }).setOrigin(0.5);

      const youScore = this.add.text(W / 2 - cardW * 0.2, val1Y, `${playerScore ?? 0}`, {
        fontSize: `${44 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: '#9933cc', stroke: '#f0e8ff', strokeThickness: 3 * dpr,
      }).setOrigin(0.5);

      const sep = this.add.text(W / 2, val1Y, '—', {
        fontSize: `${28 * sd}px`, fontFamily: 'Georgia, sans-serif', color: '#aa88bb',
      }).setOrigin(0.5);

      const botScoreEl = this.add.text(W / 2 + cardW * 0.2, val1Y, `${botScore ?? 0}`, {
        fontSize: `${44 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: '#cc8833', stroke: '#fff0dd', strokeThickness: 3 * dpr,
      }).setOrigin(0.5);

      allItems.push(lbl1, youScore, sep, botScoreEl);
    } else {
      // ── Solo card: original layout ────────────────────────────────
      const div1 = this.add.graphics();
      div1.lineStyle(1.5 * dpr, 0xddbbff, 1);
      div1.lineBetween(W / 2 - cardW * 0.35, lbl1Y - gap * 0.38, W / 2 + cardW * 0.35, lbl1Y - gap * 0.38);

      const lbl1 = this.add.text(W / 2, lbl1Y, 'Foods Stacked', {
        fontSize: `${13 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', color: '#9966cc',
      }).setOrigin(0.5);

      const val1 = this.add.text(W / 2 - cardW * 0.15, val1Y, `${foods}`, {
        fontSize: `${44 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: '#9933cc', stroke: '#f0e8ff', strokeThickness: 3 * dpr,
      }).setOrigin(0.5);

      const best1 = this.add.text(W / 2 + cardW * 0.22, val1Y, `best\n${this.highFoods}`, {
        fontSize: `${14 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', color: '#aa88cc', align: 'center',
      }).setOrigin(0.5);

      const div2 = this.add.graphics();
      div2.lineStyle(1.5 * dpr, 0xcce0ff, 1);
      div2.lineBetween(W / 2 - cardW * 0.35, lbl2Y - gap * 0.38, W / 2 + cardW * 0.35, lbl2Y - gap * 0.38);

      const lbl2 = this.add.text(W / 2, lbl2Y, 'Tower Height', {
        fontSize: `${13 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', color: '#9977cc',
      }).setOrigin(0.5);

      const val2 = this.add.text(W / 2 - cardW * 0.15, val2Y, `${towerHeight}`, {
        fontSize: `${44 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: '#8855cc', stroke: '#ddd0ff', strokeThickness: 3 * dpr,
      }).setOrigin(0.5);

      const best2 = this.add.text(W / 2 + cardW * 0.22, val2Y, `best\n${this.highHeight}`, {
        fontSize: `${14 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', color: '#aa99cc', align: 'center',
      }).setOrigin(0.5);

      allItems.push(div1, lbl1, val1, best1, div2, lbl2, val2, best2);
    }

    // ── Play Again button ──────────────────────────────────────────
    const div3 = this.add.graphics();
    div3.lineStyle(1.5 * dpr, 0xddbbff, 0.7);
    div3.lineBetween(W / 2 - cardW * 0.35, playBtnY - gap * 0.38, W / 2 + cardW * 0.35, playBtnY - gap * 0.38);

    const btnW2 = Math.min(240 * sd, W * 0.6);
    const btnH2 = 56 * sd;

    const playGfx = this.add.graphics();
    const drawPlayBtn = (col) => {
      playGfx.clear();
      playGfx.fillStyle(col, 1);
      playGfx.fillRoundedRect(W / 2 - btnW2 / 2, playBtnY - btnH2 / 2, btnW2, btnH2, btnH2 / 2);
      playGfx.fillStyle(0xffffff, 0.18);
      playGfx.fillRoundedRect(W / 2 - btnW2 / 2 + 5 * sd, playBtnY - btnH2 / 2 + 4 * sd, btnW2 - 10 * sd, btnH2 * 0.38, btnH2 * 0.3);
    };
    drawPlayBtn(0x9944cc);

    const playHit = this.add.rectangle(W / 2, playBtnY, btnW2, btnH2).setInteractive({ useHandCursor: true });
    const btnText = this.add.text(W / 2, playBtnY, '▶  Play Again', {
      fontSize: `${22 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
      fontStyle: 'bold', color: '#ffffff', stroke: '#7711aa', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    playHit.on('pointerover', () => drawPlayBtn(0xbb77ff));
    playHit.on('pointerout',  () => drawPlayBtn(0x9944cc));
    playHit.on('pointerdown', () => { const g = this.scene.get('GameScene'); if (g) g.restartGame(); });

    // ── Home button ────────────────────────────────────────────────
    const homeBtnW = Math.min(200 * sd, W * 0.5);
    const homeBtnH = 48 * sd;

    const homeGfx = this.add.graphics();
    const drawHomeBtn = (col) => {
      homeGfx.clear();
      homeGfx.fillStyle(col, 1);
      homeGfx.fillRoundedRect(W / 2 - homeBtnW / 2, homeBtnY - homeBtnH / 2, homeBtnW, homeBtnH, homeBtnH / 2);
      homeGfx.fillStyle(0xffffff, 0.15);
      homeGfx.fillRoundedRect(W / 2 - homeBtnW / 2 + 4 * sd, homeBtnY - homeBtnH / 2 + 3 * sd, homeBtnW - 8 * sd, homeBtnH * 0.38, homeBtnH * 0.3);
    };
    drawHomeBtn(0x9977dd);

    const homeHit     = this.add.rectangle(W / 2, homeBtnY, homeBtnW, homeBtnH).setInteractive({ useHandCursor: true });
    const homeBtnText = this.add.text(W / 2, homeBtnY, '🏠  Home', {
      fontSize: `${19 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
      fontStyle: 'bold', color: '#ffffff',
    }).setOrigin(0.5);

    homeHit.on('pointerover', () => drawHomeBtn(0xbbaaff));
    homeHit.on('pointerout',  () => drawHomeBtn(0x9977dd));
    homeHit.on('pointerdown', () => {
      // Track qualifying losses for interstitial threshold (solo only, ≥4 foods)
      if (this.gameMode === 'solo' && foods >= 4) {
        this.qualifyingLosses++;
      }

      const goHome = () => {
        this.scene.stop('GameScene');
        this.scene.start('HomeScene', { highFoods: this.highFoods, highHeight: this.highHeight });
        this.scene.stop('UIScene');
      };

      // Show interstitial every 2nd qualifying loss
      if (this.qualifyingLosses >= 2) {
        this.qualifyingLosses = 0;
        showInterstitial(goHome);
      } else {
        goHome();
      }
    });

    allItems.push(div3, playGfx, playHit, btnText, homeGfx, homeHit, homeBtnText);
    this.gameOverContainer.add(allItems);

    this.gameOverContainer.setAlpha(0);
    this.tweens.add({ targets: this.gameOverContainer, alpha: 1, duration: 350, ease: 'Cubic.easeOut' });
  }

  /* ─────────────────── RESCUE OFFER (Rewarded Ad) ─────────────── */
  showRescueOffer() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.rotateBtn)     this.rotateBtn.setVisible(false);
    if (this.rotateBtnText) this.rotateBtnText.setVisible(false);
    if (this.previewNextBtn) this.previewNextBtn.setVisible(false);

    this.rescueContainer = this.add.container(0, 0).setDepth(25);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 52 * sd;

    const titleY   = H / 2 - gap * 1.8;
    const subY     = titleY   + gap * 0.85;
    const timerY   = subY     + gap * 1.0;
    const saveBtnY = timerY   + gap * 1.1;
    const giveUpY  = saveBtnY + gap * 0.85;

    const cardH       = giveUpY - titleY + gap * 1.5;
    const cardCentreY = (titleY + giveUpY) / 2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2 - gap * 0.5;
    const cr          = 22 * sd;

    const overlay = this.add.graphics();
    overlay.fillStyle(0xf8f0ff, 0.88);
    overlay.fillRect(0, 0, W, H);

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * dpr, 0xff6644, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0xff6644, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    const titleTxt = this.add.text(W / 2, titleY, '💔  Tower Fell!', {
      fontSize:        `${24 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#cc4422',
      stroke:          '#ffeedd',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY,
      'Watch a short ad to restore\nyour tower and keep going!', {
      fontSize:  `${13 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#886677',
      align:     'center',
    }).setOrigin(0.5);

    const timerTxt = this.add.text(W / 2, timerY, '5', {
      fontSize:        `${46 * sd}px`,
      fontFamily:      'Arial Rounded MT Bold, Arial, sans-serif',
      color:           '#9933cc',
      stroke:          '#eeddff',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    // ── Save button ───────────────────────────────────────────────
    const saveBtnW = Math.min(240 * sd, W * 0.6);
    const saveBtnH = 62 * sd;

    const saveGfx = this.add.graphics();
    const drawSaveBtn = (col) => {
      saveGfx.clear();
      saveGfx.fillStyle(col, 1);
      saveGfx.fillRoundedRect(W / 2 - saveBtnW / 2, saveBtnY - saveBtnH / 2, saveBtnW, saveBtnH, saveBtnH / 2);
      saveGfx.fillStyle(0xffffff, 0.18);
      saveGfx.fillRoundedRect(W / 2 - saveBtnW / 2 + 5 * sd, saveBtnY - saveBtnH / 2 + 4 * sd, saveBtnW - 10 * sd, saveBtnH * 0.38, saveBtnH * 0.3);
    };
    drawSaveBtn(0x44bb66);

    const saveHit  = this.add.rectangle(W / 2, saveBtnY, saveBtnW, saveBtnH).setInteractive({ useHandCursor: true });
    const saveTxt  = this.add.text(W / 2, saveBtnY, '📺  Save My Tower', {
      fontSize:  `${21 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color:     '#ffffff',
      stroke:    '#226633',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    // ── Give up link ──────────────────────────────────────────────
    const giveUpTxt = this.add.text(W / 2, giveUpY, 'Give Up', {
      fontSize:  `${14 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#aa99bb',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.rescueContainer.add([
      overlay, shadow, card, strip,
      titleTxt, subTxt, timerTxt,
      saveGfx, saveHit, saveTxt,
      giveUpTxt,
    ]);

    // ── Dismiss helper ─────────────────────────────────────────────
    const dismiss = () => {
      if (countdown) countdown.remove();
      if (this.rescueContainer) {
        this.rescueContainer.destroy(true);
        this.rescueContainer = null;
      }
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.declineRescue();
    };

    // ── Save button: show rewarded ad ──────────────────────────────
    saveHit.on('pointerover', () => drawSaveBtn(0x55dd77));
    saveHit.on('pointerout',  () => drawSaveBtn(0x44bb66));
    saveHit.on('pointerdown', () => {
      if (countdown) countdown.remove();

      showRewarded(
        // onEarned — user watched the full ad
        () => {
          if (this.rescueContainer) {
            this.rescueContainer.destroy(true);
            this.rescueContainer = null;
          }
          const gameScene = this.scene.get('GameScene');
          if (gameScene) gameScene.rescueGame();
        },
        // onFailed — ad failed or dismissed early; restart short countdown
        () => {
          secondsLeft = 3;
          timerTxt.setText('3');
          countdown = this.time.addEvent({
            delay: 1000, loop: true,
            callback: () => {
              secondsLeft--;
              if (secondsLeft > 0) {
                timerTxt.setText(`${secondsLeft}`);
              } else {
                dismiss();
              }
            },
          });
        }
      );
    });

    giveUpTxt.on('pointerover',  () => giveUpTxt.setColor('#ccbbdd'));
    giveUpTxt.on('pointerout',   () => giveUpTxt.setColor('#aa99bb'));
    giveUpTxt.on('pointerdown',  () => dismiss());

    // ── Countdown 5 → 0 ───────────────────────────────────────────
    let secondsLeft = 5;
    let countdown = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        secondsLeft--;
        if (secondsLeft > 0) {
          timerTxt.setText(`${secondsLeft}`);
        } else {
          dismiss();
        }
      },
    });
  }

  /* ──────────────────── ROTATE AD OFFER ────────────────────────── */
  showRotateAdOffer() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.rotateAdContainer) return;

    this.rotateAdContainer = this.add.container(0, 0).setDepth(25);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 52 * sd;

    const titleY    = H / 2 - gap * 1.5;
    const subY      = titleY + gap * 0.85;
    const watchBtnY = subY + gap * 1.2;
    const noThanksY = watchBtnY + gap * 0.85;

    const cardH       = noThanksY - titleY + gap;
    const cardCentreY = (titleY + noThanksY) / 2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2;
    const cr          = 22 * sd;

    const overlay = this.add.graphics();
    overlay.fillStyle(0xf8f0ff, 0.88);
    overlay.fillRect(0, 0, W, H);
    const overlayHit = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.001)
      .setInteractive();  // Block clicks from going through

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * dpr, 0x9944cc, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0x9944cc, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    const titleTxt = this.add.text(W / 2, titleY, '🔄  Out of Rotations!', {
      fontSize:        `${22 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#9933cc',
      stroke:          '#eeddff',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY,
      'Watch a short ad to get\n12 more rotations!', {
      fontSize:  `${13 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#886677',
      align:     'center',
    }).setOrigin(0.5);

    // ── Watch Ad button ───────────────────────────────────────────
    const watchBtnW = Math.min(240 * sd, W * 0.6);
    const watchBtnH = 62 * sd;

    const watchGfx = this.add.graphics();
    const drawWatchBtn = (col) => {
      watchGfx.clear();
      watchGfx.fillStyle(col, 1);
      watchGfx.fillRoundedRect(W / 2 - watchBtnW / 2, watchBtnY - watchBtnH / 2, watchBtnW, watchBtnH, watchBtnH / 2);
      watchGfx.fillStyle(0xffffff, 0.18);
      watchGfx.fillRoundedRect(W / 2 - watchBtnW / 2 + 5 * sd, watchBtnY - watchBtnH / 2 + 4 * sd, watchBtnW - 10 * sd, watchBtnH * 0.38, watchBtnH * 0.3);
    };
    drawWatchBtn(0x44bb66);

    const watchHit = this.add.rectangle(W / 2, watchBtnY, watchBtnW, watchBtnH).setInteractive({ useHandCursor: true });
    const watchTxt = this.add.text(W / 2, watchBtnY, '📺  Watch Ad', {
      fontSize:  `${21 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color:     '#ffffff',
      stroke:    '#226633',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    // ── No thanks link ────────────────────────────────────────────
    const noThanksTxt = this.add.text(W / 2, noThanksY, 'No Thanks', {
      fontSize:  `${14 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#aa99bb',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.rotateAdContainer.add([
      overlay, overlayHit, shadow, card, strip,
      titleTxt, subTxt,
      watchGfx, watchHit, watchTxt,
      noThanksTxt,
    ]);

    const closeOffer = () => {
      if (this.rotateAdContainer) {
        this.rotateAdContainer.destroy(true);
        this.rotateAdContainer = null;
      }
    };

    // ── Watch Ad button: show rewarded ad ─────────────────────────
    watchHit.on('pointerover', () => drawWatchBtn(0x55dd77));
    watchHit.on('pointerout',  () => drawWatchBtn(0x44bb66));
    watchHit.on('pointerup', () => {
      showRewarded(
        // onEarned — user watched the full ad
        () => {
          closeOffer();
          const gameScene = this.scene.get('GameScene');
          if (gameScene) gameScene.refillRotations();
        },
        // onFailed — ad failed to load or user dismissed early
        () => {
          closeOffer();
        }
      );
    });

    noThanksTxt.on('pointerover',  () => noThanksTxt.setColor('#ccbbdd'));
    noThanksTxt.on('pointerout',   () => noThanksTxt.setColor('#aa99bb'));
    noThanksTxt.on('pointerup',    () => closeOffer());
  }

  /* ──────────────── PREVIEW NEXT FOOD OFFER ─────────────────────── */
  showPreviewNextFoodOffer() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.previewFoodContainer) return;

    const gameScene = this.scene.get('GameScene');
    if (!gameScene) return;

    this.previewFoodContainer = this.add.container(0, 0).setDepth(25);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 52 * sd;

    const titleY    = H / 2 - gap * 1.5;
    const subY      = titleY + gap * 0.85;
    const watchBtnY = subY + gap * 1.2;
    const noThanksY = watchBtnY + gap * 0.85;

    const cardH       = noThanksY - titleY + gap;
    const cardCentreY = (titleY + noThanksY) / 2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2;
    const cr          = 22 * sd;

    const overlay = this.add.graphics();
    overlay.fillStyle(0xf8f0ff, 0.88);
    overlay.fillRect(0, 0, W, H);
    const overlayHit = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.001)
      .setInteractive();

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * dpr, 0x9944cc, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0x9944cc, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    const titleTxt = this.add.text(W / 2, titleY, '🔮  Sneak Peek', {
      fontSize:        `${22 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#9933cc',
      stroke:          '#eeddff',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY,
      'Get a sneak peek of the\nnext food!', {
      fontSize:  `${13 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#886677',
      align:     'center',
    }).setOrigin(0.5);

    // ── Watch Ad button ───────────────────────────────────────────
    const watchBtnW = Math.min(240 * sd, W * 0.6);
    const watchBtnH = 62 * sd;

    const watchGfx = this.add.graphics();
    const drawWatchBtn = (col) => {
      watchGfx.clear();
      watchGfx.fillStyle(col, 1);
      watchGfx.fillRoundedRect(W / 2 - watchBtnW / 2, watchBtnY - watchBtnH / 2, watchBtnW, watchBtnH, watchBtnH / 2);
      watchGfx.fillStyle(0xffffff, 0.18);
      watchGfx.fillRoundedRect(W / 2 - watchBtnW / 2 + 5 * sd, watchBtnY - watchBtnH / 2 + 4 * sd, watchBtnW - 10 * sd, watchBtnH * 0.38, watchBtnH * 0.3);
    };
    drawWatchBtn(0x9944cc);

    const watchHit = this.add.rectangle(W / 2, watchBtnY, watchBtnW, watchBtnH).setInteractive({ useHandCursor: true });
    const watchTxt = this.add.text(W / 2, watchBtnY, '📺  Watch Ad', {
      fontSize:  `${21 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color:     '#ffffff',
      stroke:    '#5511aa',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    // ── No thanks link ────────────────────────────────────────────
    const noThanksTxt = this.add.text(W / 2, noThanksY, 'No Thanks', {
      fontSize:  `${14 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#aa99bb',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.previewFoodContainer.add([
      overlay, overlayHit, shadow, card, strip,
      titleTxt, subTxt,
      watchGfx, watchHit, watchTxt,
      noThanksTxt,
    ]);

    const closeOffer = () => {
      if (this.previewFoodContainer) {
        this.previewFoodContainer.destroy(true);
        this.previewFoodContainer = null;
      }
    };

    watchHit.on('pointerover', () => drawWatchBtn(0xbb77ff));
    watchHit.on('pointerout',  () => drawWatchBtn(0x9944cc));
    watchHit.on('pointerup', () => {
      showRewarded(
        // onEarned — user watched the full ad
        () => {
          closeOffer();
          const nextFood = gameScene.getNextFoodName();
          this.showNextFoodReveal(nextFood, sd, dpr);
          gameScene.nextFoodRevealed = true;
          gameScene.events.emit('nextFoodStatusUpdate', true);
        },
        // onFailed — ad failed to load or user dismissed early
        () => {
          closeOffer();
        }
      );
    });

    noThanksTxt.on('pointerover',  () => noThanksTxt.setColor('#ccbbdd'));
    noThanksTxt.on('pointerout',   () => noThanksTxt.setColor('#aa99bb'));
    noThanksTxt.on('pointerup',    () => closeOffer());
  }

  /* ──────────────────── NEXT FOOD REVEAL ────────────────────────── */
  showNextFoodReveal(foodName, sd, dpr) {
    const W = this.scale.width;
    const H = this.scale.height;

    if (this.nextFoodRevealContainer) return;

    this.nextFoodRevealContainer = this.add.container(0, 0).setDepth(26);

    const cardW = Math.min(300 * sd, W * 0.80);
    const gap   = 50 * sd;

    const titleY    = H / 2 - gap * 2.5;
    const imgY      = titleY + gap * 1.3;
    const nameY     = imgY + gap * 1.1;
    const closeBtnY = nameY + gap * 1.0;

    const cardH       = closeBtnY - titleY + gap * 1.4;
    const cardCentreY = (titleY + closeBtnY) / 2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2;
    const cr          = 22 * sd;

    const overlay = this.add.graphics();
    overlay.fillStyle(0xf8f0ff, 0.88);
    overlay.fillRect(0, 0, W, H);
    const overlayHit = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.001)
      .setInteractive();

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * dpr, 0xeec044, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0xeec044, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    const titleTxt = this.add.text(W / 2, titleY, '✨ Next Food ✨', {
      fontSize:        `${20 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#cc9911',
      stroke:          '#fff6dd',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    // Food image — fixed size, fade in
    const foodImg = this.add.image(W / 2, imgY, foodName)
      .setDisplaySize(60 * sd, 60 * sd)
      .setAlpha(0);
    this.tweens.add({
      targets: foodImg, alpha: 1, duration: 400, ease: 'Power2',
    });

    const foodNameTxt = this.add.text(W / 2, nameY, foodName.toUpperCase(), {
      fontSize:  `${18 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color:     '#7722aa',
    }).setOrigin(0.5);

    // ── Close button ──────────────────────────────────────────────
    const closeBtnW = Math.min(160 * sd, cardW * 0.55);
    const closeBtnH = 48 * sd;

    const closeGfx = this.add.graphics();
    const drawCloseBtn = (col) => {
      closeGfx.clear();
      closeGfx.fillStyle(col, 1);
      closeGfx.fillRoundedRect(W / 2 - closeBtnW / 2, closeBtnY - closeBtnH / 2, closeBtnW, closeBtnH, closeBtnH / 2);
      closeGfx.fillStyle(0xffffff, 0.18);
      closeGfx.fillRoundedRect(W / 2 - closeBtnW / 2 + 5 * sd, closeBtnY - closeBtnH / 2 + 4 * sd, closeBtnW - 10 * sd, closeBtnH * 0.38, closeBtnH * 0.3);
    };
    drawCloseBtn(0x9944cc);

    const closeHit = this.add.rectangle(W / 2, closeBtnY, closeBtnW, closeBtnH).setInteractive({ useHandCursor: true });
    const closeTxt = this.add.text(W / 2, closeBtnY, 'Got It!', {
      fontSize:  `${17 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color:     '#ffffff',
      stroke:    '#5511aa',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    this.nextFoodRevealContainer.add([
      overlay, overlayHit, shadow, card, strip,
      titleTxt, foodImg, foodNameTxt,
      closeGfx, closeHit, closeTxt,
    ]);

    closeHit.on('pointerover', () => drawCloseBtn(0xbb77ff));
    closeHit.on('pointerout',  () => drawCloseBtn(0x9944cc));
    closeHit.on('pointerup', () => {
      if (this.nextFoodRevealContainer) {
        this.nextFoodRevealContainer.destroy(true);
        this.nextFoodRevealContainer = null;
      }
    });
  }

  /* ─────────────────────────── PAUSED ────────────────────────────── */
  showPaused() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    this.pauseContainer = this.add.container(0, 0).setDepth(20);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 50 * sd;
    const titleY    = H / 2 - gap * 2.2;
    const subtitleY = titleY    + gap;
    const resumeY   = subtitleY + gap;
    const homeY     = resumeY   + gap;
    const musicY    = homeY     + gap * 0.78;
    const sfxY      = musicY    + gap * 0.72;

    const cardH       = sfxY - titleY + gap * 2.5;
    const cardCentreY = (titleY + sfxY) / 2;
    const cardX       = W / 2 - cardW / 2;
    const cardTop     = cardCentreY - cardH / 2 - gap * 0.5;
    const cr          = 22 * sd;

    const overlay = this.add.graphics();
    overlay.fillStyle(0xf8f0ff, 0.82);
    overlay.fillRect(0, 0, W, H);

    const shadow = this.add.graphics();
    shadow.fillStyle(0xcc99ee, 0.28);
    shadow.fillRoundedRect(cardX + 5 * sd, cardTop + 7 * sd, cardW, cardH, cr);

    const card = this.add.graphics();
    card.fillStyle(0xfcf8ff, 1);
    card.fillRoundedRect(cardX, cardTop, cardW, cardH, cr);
    card.lineStyle(2.5 * dpr, 0xbbaaff, 1);
    card.strokeRoundedRect(cardX, cardTop, cardW, cardH, cr);

    const strip = this.add.graphics();
    strip.fillStyle(0x9977dd, 1);
    strip.fillRoundedRect(cardX, cardTop, cardW, 8 * sd, { tl: cr, tr: cr, bl: 0, br: 0 });

    const title = this.add.text(W / 2, titleY, '⏸  Paused', {
      fontSize:        `${26 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#7733bb',
      stroke:          '#ddd0ff',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    const subtitle = this.add.text(W / 2, subtitleY, 'Your score will be lost if you quit', {
      fontSize:  `${13 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#9977bb',
      wordWrap:  { width: cardW * 0.82 },
      align:     'center',
    }).setOrigin(0.5);

    const resumeBtnW = Math.min(240 * sd, W * 0.6);
    const resumeBtnH = 56 * sd;

    const resumeGfx = this.add.graphics();
    const drawResumeBtn = (col) => {
      resumeGfx.clear();
      resumeGfx.fillStyle(col, 1);
      resumeGfx.fillRoundedRect(W / 2 - resumeBtnW / 2, resumeY - resumeBtnH / 2, resumeBtnW, resumeBtnH, resumeBtnH / 2);
      resumeGfx.fillStyle(0xffffff, 0.18);
      resumeGfx.fillRoundedRect(W / 2 - resumeBtnW / 2 + 5 * sd, resumeY - resumeBtnH / 2 + 4 * sd, resumeBtnW - 10 * sd, resumeBtnH * 0.38, resumeBtnH * 0.3);
    };
    drawResumeBtn(0x9944cc);

    const resumeHit  = this.add.rectangle(W / 2, resumeY, resumeBtnW, resumeBtnH).setInteractive({ useHandCursor: true });
    const resumeText = this.add.text(W / 2, resumeY, '▶  Resume', {
      fontSize:  `${22 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color:     '#ffffff',
      stroke:    '#7711aa',
      strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    resumeHit.on('pointerover',  () => drawResumeBtn(0xbb77ff));
    resumeHit.on('pointerout',   () => drawResumeBtn(0x9944cc));
    resumeHit.on('pointerdown',  () => { const g = this.scene.get('GameScene'); if (g) g.resumeGame(); });

    const homeBtnW = Math.min(200 * sd, W * 0.5);
    const homeBtnH = 48 * sd;

    const homeGfx = this.add.graphics();
    const drawHomeBtn = (col) => {
      homeGfx.clear();
      homeGfx.fillStyle(col, 1);
      homeGfx.fillRoundedRect(W / 2 - homeBtnW / 2, homeY - homeBtnH / 2, homeBtnW, homeBtnH, homeBtnH / 2);
      homeGfx.fillStyle(0xffffff, 0.15);
      homeGfx.fillRoundedRect(W / 2 - homeBtnW / 2 + 4 * sd, homeY - homeBtnH / 2 + 3 * sd, homeBtnW - 8 * sd, homeBtnH * 0.38, homeBtnH * 0.3);
    };
    drawHomeBtn(0x9977dd);

    const homeHit     = this.add.rectangle(W / 2, homeY, homeBtnW, homeBtnH).setInteractive({ useHandCursor: true });
    const homeBtnText = this.add.text(W / 2, homeY, '🏠  Home', {
      fontSize:  `${19 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'bold',
      color:     '#ffffff',
    }).setOrigin(0.5);

    homeHit.on('pointerover',  () => drawHomeBtn(0xbbaaff));
    homeHit.on('pointerout',   () => drawHomeBtn(0x9977dd));
    homeHit.on('pointerdown',  () => {
      this.scene.stop('GameScene');
      this.scene.stop('UIScene');
      this.scene.start('HomeScene');
    });

    // ── Music toggle button ────────────────────────────────────────
    const toggleBtnW = Math.min(190 * sd, W * 0.46);
    const toggleBtnH = 38 * sd;

    const musicBtn = this.add.graphics();
    const drawMusicToggle = (col) => {
      musicBtn.clear();
      musicBtn.fillStyle(col, 0.92);
      musicBtn.fillRoundedRect(W / 2 - toggleBtnW / 2, musicY - toggleBtnH / 2, toggleBtnW, toggleBtnH, toggleBtnH / 2);
    };
    drawMusicToggle(0x5533aa);

    const musicBtnHit  = this.add.rectangle(W / 2, musicY, toggleBtnW, toggleBtnH).setInteractive({ useHandCursor: true });
    const musicBtnText = this.add.text(W / 2, musicY,
      musicManager.isMuted() ? '🔇  Music Off' : '🔊  Music On', {
        fontSize: `${14 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', color: '#ddc8ff',
      }
    ).setOrigin(0.5);

    musicBtnHit.on('pointerover',  () => drawMusicToggle(0x7755cc));
    musicBtnHit.on('pointerout',   () => drawMusicToggle(0x5533aa));
    musicBtnHit.on('pointerdown',  () => {
      musicManager.setMuted(!musicManager.isMuted());
      musicBtnText.setText(musicManager.isMuted() ? '🔇  Music Off' : '🔊  Music On');
      drawMusicToggle(0x5533aa);
    });

    // ── Effects toggle button ──────────────────────────────────────
    const sfxBtn = this.add.graphics();
    const drawSfxToggle = (col) => {
      sfxBtn.clear();
      sfxBtn.fillStyle(col, 0.92);
      sfxBtn.fillRoundedRect(W / 2 - toggleBtnW / 2, sfxY - toggleBtnH / 2, toggleBtnW, toggleBtnH, toggleBtnH / 2);
    };
    drawSfxToggle(0x5533aa);

    const sfxBtnHit  = this.add.rectangle(W / 2, sfxY, toggleBtnW, toggleBtnH).setInteractive({ useHandCursor: true });
    const sfxBtnText = this.add.text(W / 2, sfxY,
      musicManager.isSfxMuted ? '🔇  Effects Off' : '🔊  Effects On', {
        fontSize: `${14 * sd}px`, fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic', color: '#ddc8ff',
      }
    ).setOrigin(0.5);

    sfxBtnHit.on('pointerover',  () => drawSfxToggle(0x7755cc));
    sfxBtnHit.on('pointerout',   () => drawSfxToggle(0x5533aa));
    sfxBtnHit.on('pointerdown',  () => {
      musicManager.setSfxMuted(!musicManager.isSfxMuted);
      sfxBtnText.setText(musicManager.isSfxMuted ? '🔇  Effects Off' : '🔊  Effects On');
      drawSfxToggle(0x5533aa);
    });

    this.pauseContainer.add([
      overlay, shadow, card, strip, title, subtitle,
      resumeGfx, resumeHit, resumeText,
      homeGfx, homeHit, homeBtnText,
      musicBtn, musicBtnHit, musicBtnText,
      sfxBtn, sfxBtnHit, sfxBtnText,
    ]);

    this.pauseContainer.setAlpha(0);
    this.tweens.add({ targets: this.pauseContainer, alpha: 1, duration: 300, ease: 'Cubic.easeOut' });
  }
}
